/**
 * Die Seite "Dashboard" als nachgeladener Chunk: `DashboardSeite.tsx` samt Rechenmodul, Frische und Kacheln
 * gehoert nicht ins Eintrittsbuendel (Grenze und NEGATIVLISTE Kategorie N in `test/reference/bundle.test.ts`), erst der Reiter
 * lädt sie. Verwendet von `App.tsx` (`#page-dashboard`, nur bei aktiver Seite); Vorbild ist `Drawer/DocsTab/Massenupload/
 * MassenuploadDialogLazy.tsx`. Das Stylesheet (`styles/neubau/dashboard.css`) laedt `main.tsx`, nicht diese Datei.
 *
 * Diese Huelle liegt im Einstieg und importiert die Seite nur zur Laufzeit und ihre Props nur als Typ (`import type` wird
 * geloescht): ein statischer Import der Seite zoege sie samt Rechenmodul ins Eintrittsbuendel. Umgekehrt importiert KEIN Modul des
 * Chunks diese Huelle, `state/fehlerStapel.ts`, `api/useDateiDownload.ts` oder `api/dashboard.ts` (AGENTS.md, „Rolldown zerlegt
 * das Eintrittsbuendel“; `api/dashboard.ts` zieht `api/basis.ts` und damit den Gemeinschafts-Chunk `basis-*`); den Download der
 * Packliste und den Abruf des DSV-Standes fuehrt deshalb `App.tsx` aus und reicht beide als `onDateiLaden` und `standLaden` herein.
 *
 * ================= Wenn der Chunk nicht laedt =================
 *
 * Ein alter Tab nach einem Deployment kennt `DashboardSeite-<hash>.js` nicht mehr: `import()` scheitert, `React.lazy` wirft beim
 * Rendern. Ohne Grenze dazwischen faengt das erst die Wurzelgrenze (`FehlerGrenze` in `src/App.tsx`) und ersetzt die GANZE
 * Anwendung durch eine Fehlerflaeche - auch die Kopfnavigation, mit der man auf eine andere Seite ausweichen koennte. Das Dashboard
 * ist eine Seite unter acht; Board und Drawer sollen stehen bleiben. `SeitenGrenze` ersetzt nur die Seitenflaeche durch „Das
 * Dashboard konnte nicht geladen werden.“ samt Knopf „Neu laden“ und meldet den Fehler im Stapel (bei einem Chunk-Ladefehler
 * haengt der Uebersetzer `fehlertext` den Hinweis `FEHLERTEXTE.neuLaden` an). Die Grenze faengt auch einen Absturz der Seite mitten
 * im Lauf, nicht nur den Ladefehler. `React.lazy` merkt sich die Ablehnung: ein Wechsel auf eine andere Seite und zurueck zeigt
 * dieselbe Flaeche, erst „Neu laden“ holt den neuen Stand.
 */
import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, JSX, ReactNode } from "react";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import type { DashboardSeiteProps } from "./DashboardSeite.tsx";

const DashboardSeiteChunk = lazy(() => import("./DashboardSeite.tsx"));

const FEHLERTEXT = "Das Dashboard konnte nicht geladen werden.";

class SeitenGrenze extends Component<{ children: ReactNode }, { abgestuerzt: boolean }> {
  state = { abgestuerzt: false };

  static getDerivedStateFromError(): { abgestuerzt: boolean } {
    return { abgestuerzt: true };
  }

  componentDidCatch(fehler: unknown, info: ErrorInfo): void {
    console.error("Dashboard abgestürzt:", fehler, info.componentStack);
    meldeFehler(fehler, FEHLERTEXT);
  }

  render(): ReactNode {
    if (!this.state.abgestuerzt) return this.props.children;
    // Die Klassen der gemeinsamen Ladefehler-Flaeche (`styles/neubau/ladefehler.css`, ueber `main.tsx`); die Flaeche selbst
    // (`Ladefehler.tsx`) fuehrt „Erneut laden“ als Neuversuch der Flaeche - hier hilft nur das Neuladen der Seite.
    return (
      <div className="ladefehler" role="alert">
        <p className="ladefehler-text">{FEHLERTEXT}</p>
        <button type="button" className="btn-ghost ladefehler-knopf" onClick={() => window.location.reload()}>
          Neu laden
        </button>
      </div>
    );
  }
}

export function DashboardSeiteLazy(props: DashboardSeiteProps): JSX.Element {
  return (
    <SeitenGrenze>
      <Suspense
        fallback={
          <p className="dash-leer" role="status">
            Dashboard wird geladen …
          </p>
        }
      >
        <DashboardSeiteChunk {...props} />
      </Suspense>
    </SeitenGrenze>
  );
}
